import React, { useEffect, useRef } from "react";

export const AudioWaveform = ({ analyser, isRecording }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!isRecording || !analyser || !canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const canvasContext = canvas.getContext("2d");
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvasContext.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;

    const barCount = 40;
    const barWidth = 3;
    const barGap = 2;

    const draw = () => {
      if (!isRecording) {
        return;
      }

      animationRef.current = requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      canvasContext.clearRect(0, 0, width, height);

      const totalWidth = barCount * (barWidth + barGap);
      const startX = (width - totalWidth) / 2;

      for (let i = 0; i < barCount; i++) {
        const index = Math.floor((i * bufferLength) / barCount);
        const value = dataArray[index];

        const normalized = value / 255;

        const barHeight = Math.max(2, normalized * height * 0.8);

        const x = startX + i * (barWidth + barGap);

        const y = (height - barHeight) / 2;

        const gradient = canvasContext.createLinearGradient(0, y, 0, y + barHeight);
        gradient.addColorStop(0, "rgba(59, 130, 246, 0.8)");
        gradient.addColorStop(1, "rgba(96, 165, 250, 0.6)");

        canvasContext.fillStyle = gradient;
        canvasContext.fillRect(x, y, barWidth, barHeight);
      }
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyser, isRecording]);

  if (!isRecording) {
    return null;
  }

  return (
    <div className="gpc-audio-waveform-container">
      <canvas
        ref={canvasRef}
        className="gpc-audio-waveform-canvas"
        aria-label="Audio waveform visualization"
      />
    </div>
  );
};
